<template lang="pug">
.teaser.relative
    .image.flex.items-center(v-if="image")
        figure
            img(
                v-if="showImage"
                v-lazy="image"
                :title="title"
                :width="image.width"
                :height="image.height"
            )
    .image.placeholder(v-else)
        figure
    .text.rte-area.flex.items-center
        article
            h3.title(v-if="title") {{ title }}
            .bait(v-if="text")
                p.content(v-html="text")
            small.distance {{ distance }}
</template>

<script>
import countries from 'i18n-iso-countries';
import de from 'i18n-iso-countries/langs/de.json';
countries.registerLocale(de);

import { get, trim, round } from '@/helpers';

export default {
    name: 'Teaser',
    data() {
        return {
            delay: 150,
            mouseover: false,
            showImage: true,
            imageHeight: 140
        };
    },
    props: {
        teaser: {
            type: Object,
            required: true
        },
        display: {
            type: String,
            default: 'row'
        }
    },
    watch: {
        image() {
            // ugly workaround
            this.showImage = false;
            this.$nextTick(() => {
                this.showImage = true;
            });
        }
    },
    computed: {
        image() {
            let image;

            const media = get(this.teaser, 'member.media');
            if (media && media.length) {
                const first = media.find(
                    media => media.scope === 'avatar' && media.type === 'image' && media.image != null
                );
                if (first) image = first.image;
            }

            if (!image) return false;

            let fit = get(image, 'settings.process.fit') || 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1';
            const parameters = {
                ...get(fit.match(/x=(?<fx>[0-1]?\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/y=(?<fy>[0-1]?\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/z=(?<fz>[1-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/w=(?<w>[0-9]+\.?[0-9]*)/), 'groups'),
                ...get(fit.match(/h=(?<h>[0-9]+\.?[0-9]*)/), 'groups')
            };
            // let { w, h, fx, fy, fz } = parameters
            let { w, h, fz } = parameters;
            if (!fz) fz = 1;

            let width, height, aspectRatio, devicePixelRatio;

            width = image.width / fz;
            height = image.height / fz;
            aspectRatio = width / height;
            devicePixelRatio = window.devicePixelRatio;

            if (w && h) {
                aspectRatio = w / h;
                // height = Math.min(this.imageWidth, width) / aspectRatio;
                width = Math.min(this.imageHeight * aspectRatio, width);

                fit = fit
                    .replace(/w=([0-9]+\.?[0-9]*)/, `w=${width * devicePixelRatio}`)
                    .replace(/h=([0-9]+\.?[0-9]*)/, `h=${height * devicePixelRatio}`);
            } else if (!w && !h) {
                // fit += `&w=${Math.min(this.imageWidth * devicePixelRatio, width * devicePixelRatio)}`;
                fit += `&h=${Math.min(this.imageHeight * devicePixelRatio, height * devicePixelRatio)}`;
            }

            const storageRoute = trim(
                `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                '/'
            );

            return {
                width,
                height,
                src: `${storageRoute}/images/${image.id}/${image.source.name}?${fit}`,
                error: `${storageRoute}/images/${image.id}/${image.source.name}?${fit}&preview=greyscale`,
                loading: `${storageRoute}/images/${image.id}/${image.source.name}?${fit}&preview=blur`
            };
        },
        distance() {
            const distance = get(this.teaser, 'distance');
            if (distance) return `${round(distance, 2)} km`;
            else return null;
        },
        title() {
            return get(this.teaser, 'company') || get(this.teaser, 'member.company') || get(this.teaser, 'member.name');
        },
        text() {
            let text = '';

            text += `<p>${get(this.teaser, 'street')}</p>`;
            text += `<p>${get(this.teaser, 'zipcode')} ${get(this.teaser, 'city')}</p>`;

            const country = get(this.teaser, 'country');
            if (country) text += `<p>${countries.getName(country, 'de')}</p>`;

            const phone = get(this.teaser, 'phone');
            let url = get(this.teaser, 'url');

            if (phone || url) text += '<div class="mt"></div>';

            // <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path d="M0 141.3c0-8.5 3.4-16.6 9.9-22.1C38.2 95.4 125.9 32 256 32s217.8 63.4 246.1 87.2c6.5 5.5 9.9 13.6 9.9 22.1V176c0 17.7-14.3 32-32 32H437.7c-13.1 0-24.9-8-29.7-20.1l-19.4-48.6c-2.9-7.3-8.4-13.2-15.7-16c-18.6-7-59.5-19.3-116.8-19.3s-98.2 12.3-116.8 19.3c-7.3 2.8-12.8 8.7-15.7 16L104 187.9C99.2 200 87.4 208 74.3 208H32c-17.7 0-32-14.3-32-32V141.3zM32 432V401c0-11.1 2.9-22.1 8.4-31.8l101.1-177C153 172.3 174.2 160 197.1 160H314.9c23 0 44.2 12.3 55.6 32.2l101.1 177c5.5 9.7 8.4 20.6 8.4 31.8v31c0 26.5-21.5 48-48 48H80c-26.5 0-48-21.5-48-48zM328 312a72 72 0 1 0 -144 0 72 72 0 1 0 144 0z"/></svg>
            // <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path class="fa-secondary" opacity=".4" d="M505.7 412.3l-8.5 31.2c-71.8-8.7-138.9-32.9-197.8-69.1l20.5-25c55.4 33.4 118.4 55.4 185.8 62.9zM99.7 6.3c7.5 67.4 29.5 130.4 62.9 185.8l-25 20.5C101.4 153.7 77.2 86.6 68.4 14.8L99.7 6.3z"/><path class="fa-primary" d="M68.4 14.8l-39 10.6C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l10.6-39C273.2 416.4 95.6 238.8 68.4 14.8zM505.7 412.3l4.9-17.8c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6l-25.1 30.7c55.4 33.4 118.4 55.4 185.8 62.9zM162.6 192.1L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96c-7.7-18.6-28-28.5-47.4-23.2L99.7 6.3c7.5 67.4 29.5 130.4 62.9 185.8z"/></svg>
            // <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path d="M32 142.4V192h72.9L86.6 224H32c-17.7 0-32-14.3-32-32V141.3c0-8.5 3.4-16.6 9.9-22.1C38.2 95.4 125.9 32 256 32s217.8 63.4 246.1 87.2c6.5 5.5 9.9 13.6 9.9 22.1V192c0 17.7-14.3 32-32 32H425.4l-18.3-32H480V142.4C453 120.1 373.2 64 256 64S59 120.1 32 142.4zm0-1.1s0 0 0 0v0zM32 401c0-11.1 2.9-22.1 8.4-31.8l101.1-177c10.5-18.4 29.5-30.4 50.4-32V128c0-8.8 7.2-16 16-16s16 7.2 16 16v32h64V128c0-8.8 7.2-16 16-16s16 7.2 16 16v32.2c21 1.7 39.9 13.6 50.4 32l101.1 177c5.5 9.7 8.4 20.6 8.4 31.8v31c0 26.5-21.5 48-48 48H80c-26.5 0-48-21.5-48-48V401zM304 192H208 197.1c-11.5 0-22.1 6.2-27.8 16.1L68.2 385.1C65.5 390 64 395.4 64 401v31c0 8.8 7.2 16 16 16H432c8.8 0 16-7.2 16-16V401c0-5.6-1.5-11-4.2-15.9l-101.1-177c-5.7-10-16.3-16.1-27.8-16.1H304zM208 320a48 48 0 1 0 96 0 48 48 0 1 0 -96 0zm48 80a80 80 0 1 1 0-160 80 80 0 1 1 0 160z"/></svg>
            if (phone) {
                text += `<p class="nowrap">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM186.7 128.7l-44 12C134 143.1 128 151 128 160c0 123.7 100.3 224 224 224c9 0 16.9-6 19.3-14.7l12-44c2.6-9.7-2.3-19.9-11.6-23.7l-48-20c-8.2-3.4-17.6-1-23.2 5.8L280.3 312c-35.2-16.6-63.7-45.1-80.3-80.3l24.7-20.2c6.8-5.6 9.2-15 5.8-23.2l-20-48c-3.9-9.3-14-14.2-23.7-11.6z"/></svg>
                    <a href="tel:${phone}">${phone}</a>
                </p>`;
            }

            if (url && url.trim(' ').indexOf(' ') === -1) {
                if (!url.match(/^https?:\/\//)) url = `http://${url}`;
                text += `<p class="nowrap">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--!Font Awesome Pro 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path d="M256 464C141.1 464 48 370.9 48 256c0-22 3.4-43.1 9.7-63l9.4 16.4c8.3 14.5 21.9 25.2 38 29.8L163 255.7c17.2 4.9 29 20.6 29 38.5v39.9c0 11 6.2 21 16 25.9s16 14.9 16 25.9v30.4c0 15.9 15.2 27.3 30.5 23c15.9-4.5 28.3-17 32.8-32.8l1.5-5.4c4.6-16.1 15.3-29.7 29.8-38l9.2-5.3c15-8.5 24.2-24.5 24.2-41.7v-8.3c0-12.7-5.1-24.9-14.1-33.9l-3.9-3.9c-9-9-21.2-14.1-33.9-14.1H257c-11.1 0-22.1-2.9-31.8-8.4l-34.5-19.7c-4.3-2.5-7.6-6.5-9.2-11.2c-3.2-9.6 1.1-20 10.2-24.5l5.9-3c6.6-3.3 14.3-3.9 21.3-1.5l23.2 7.7c8.2 2.7 17.2-.4 21.9-7.5c4.7-7 4.2-16.3-1.2-22.8l-13.6-16.3c-10-12-9.9-29.5 .3-41.3l15.7-18.3c8.8-10.3 10.2-25 3.5-36.7l-2.4-4.2c73.6 3.6 137.2 45.6 171.2 106.3L412 164.8c-15.7 6.3-23.8 23.8-18.5 39.8l16.9 50.7c3.5 10.4 12 18.3 22.6 20.9l29.1 7.3C448.7 385.4 361.5 464 256 464zm0 48A256 256 0 1 0 256 0a256 256 0 1 0 0 512z"/></svg>
                    <a href="${url}">${this.teaser.url}</a>
                </p>`;
            }

            return text;
        }
    }
};
</script>

<style lang="sass" nolang="scss" scoped>
@import '~@/assets/sass/variables'

h3.title
    font-size: 1.25rem
    font-weight: 600
    margin-bottom: 0.5rem
    margin-right: 3rem

.teaser
    color: $cc-font-base
    background: theme("colors.gray.100")
    border-radius: 4px
    &::v-deep
        svg
            height: 1.2rem
            fill: currentColor

        .nowrap
            white-space: nowrap
            display: flex
            align-items: center
            gap: 6px
        .mt
            margin-top: 0.5rem
    .text
        padding: 1rem
        // padding-left: 0
        min-width: 0

    .image.placeholder
        background: $skeletonColor
        border-radius: 4px
        border-top-right-radius: 0
        border-bottom-right-radius: 0

.reseller-list.show-images .teaser
    .text
        padding-left: 0

figure
    padding: 1rem
    padding-right: 0
    height: 140px
    width: auto
    max-width: 200px
    aspect-ratio: 12 / 9
    border-radius: 4px

img
    object-fit: scale-down
    width: 100%
    height: 100%
    // height: 140px
    // width: auto
    border-radius: 4px

article
    width: 100%

.distance
    @apply absolute
    right: 10px
    top: 10px

.bait::v-deep
    svg
        flex-shrink: 0
    a
        display: inline-block
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap
</style>
